"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.filterAndSortListRows = exports.getListFilterResultCounts = exports.getListFilterResultCount = exports.getListFilterValues = exports.getListCellBadgeTone = exports.buildListViewRows = exports.buildListSearchMatcher = void 0;
var prioritisation_1 = require("../../shared/utils/prioritisation");
var field_coercion_1 = require("../../UseCaseDetails/utils/field-coercion");
/**
 * Reads a display-ready string value from an item's field map.
 *
 * Use-case fields can arrive as plain strings, numbers, person objects
 * (e.g. SharePoint `{ Title, Email }`), or JSON-encoded identity refs
 * (`'{"displayName":"…"}'`). Delegating to `coerceFieldValueToString`
 * keeps the list view from rendering `[object Object]` or raw JSON
 * blobs in cells like Owner.
 */
var getItemFieldValue = function (item, field, defaultValue) {
    if (item && item[field] !== undefined && item[field] !== null) {
        var coerced = field_coercion_1.coerceFieldValueToString(item[field]);
        if (coerced.trim()) {
            return coerced;
        }
    }
    if (defaultValue) {
        return field + ": " + defaultValue;
    }
    return '';
};
var EMPTY_VALUE = '-';
var SEARCHABLE_ROW_KEYS = [
    'id',
    'name',
    'prioritisation',
    'complexity',
    'priority',
    'owner',
    'stage',
    'sourceSystem',
    'clientGroup',
    'businessFunction',
];
var getSearchTerms = function (searchText) {
    return searchText.trim().toLowerCase().split(/\s+/).filter(Boolean);
};
var escapeSearchTerm = function (value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};
exports.buildListSearchMatcher = function (searchText) {
    var terms = getSearchTerms(searchText);
    if (terms.length === 0) {
        return null;
    }
    var splitter = new RegExp("(" + terms.map(escapeSearchTerm).join('|') + ")", 'ig');
    var normalizedTerms = terms.reduce(function (result, term) {
        result[term] = true;
        return result;
    }, {});
    return { splitter: splitter, normalizedTerms: normalizedTerms };
};
var normalizeToken = function (value) {
    return value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');
};
var normalizeValue = function (value) {
    var trimmedValue = value.trim();
    return trimmedValue || EMPTY_VALUE;
};
var getFieldValue = function (useCase, fieldNames) {
    for (var _i = 0, fieldNames_1 = fieldNames; _i < fieldNames_1.length; _i++) {
        var fieldName = fieldNames_1[_i];
        var value = getItemFieldValue(useCase.fields, fieldName);
        if (value) {
            return normalizeValue(value);
        }
    }
    return EMPTY_VALUE;
};
var getNumericValue = function (value) {
    if (/^\d+(?:\.\d+)?$/.test(value)) {
        return Number(value);
    }
    return null;
};
var getComplexityBadgeTone = function (value) {
    var numericValue = getNumericValue(value);
    if (numericValue !== null) {
        if (numericValue >= 7) {
            return 'severity-high';
        }
        if (numericValue >= 4) {
            return 'severity-medium';
        }
        return 'severity-low';
    }
    var normalizedValue = normalizeToken(value);
    if (normalizedValue.indexOf('high') >= 0) {
        return 'severity-high';
    }
    if (normalizedValue.indexOf('medium') >= 0 || normalizedValue === 'med') {
        return 'severity-medium';
    }
    if (normalizedValue.indexOf('low') >= 0) {
        return 'severity-low';
    }
    return null;
};
var getPriorityBadgeTone = function (value) {
    var numericValue = getNumericValue(value);
    if (numericValue !== null) {
        if (numericValue <= 1) {
            return 'severity-high';
        }
        if (numericValue === 2) {
            return 'severity-medium';
        }
        return 'severity-low';
    }
    return getComplexityBadgeTone(value);
};
var getPrioritisationBadgeTone = function (value) {
    var numericValue = getNumericValue(value);
    if (numericValue !== null) {
        if (numericValue >= 0.67) {
            return 'severity-high';
        }
        if (numericValue >= 0.34) {
            return 'severity-medium';
        }
        return 'severity-low';
    }
    return null;
};
var getStageBadgeTone = function (value) {
    var normalizedValue = normalizeToken(value);
    if (normalizedValue === 'submitted') {
        return 'status-submitted';
    }
    if (normalizedValue === 'qualified') {
        return 'status-qualified';
    }
    if (normalizedValue === 'approved' ||
        normalizedValue === 'completed' ||
        normalizedValue === 'complete') {
        return 'status-approved';
    }
    if (normalizedValue === 'in-progress' ||
        normalizedValue === 'active' ||
        normalizedValue === 'ongoing') {
        return 'status-active';
    }
    if (normalizedValue === 'rejected' || normalizedValue === 'blocked') {
        return 'status-rejected';
    }
    return 'status-neutral';
};
var getClientGroupBadgeTone = function (value) {
    var normalizedValue = normalizeToken(value);
    if (normalizedValue === 'avanade') {
        return 'group-avanade';
    }
    if (normalizedValue === 'n-a' || normalizedValue === 'na') {
        return 'group-industry';
    }
    return 'group-neutral';
};
var getBusinessFunctionBadgeTone = function (value) {
    var normalizedValue = normalizeToken(value);
    if (normalizedValue === 'delivery') {
        return 'function-delivery';
    }
    if (normalizedValue === 'operations') {
        return 'function-operations';
    }
    if (normalizedValue === 'client') {
        return 'function-client';
    }
    if (normalizedValue === 'migrations') {
        return 'function-migrations';
    }
    return 'function-neutral';
};
/**
 * Resolve the source/system value for a use case from its workflow.
 */
var getSourceSystemValue = function (useCase) {
    return normalizeValue("" + (useCase.workflow || ''));
};
var compareRowValues = function (leftRow, rightRow, sortKey) {
    var leftValue = leftRow[sortKey];
    var rightValue = rightRow[sortKey];
    var leftNumericValue = getNumericValue(leftValue);
    var rightNumericValue = getNumericValue(rightValue);
    if (leftNumericValue !== null && rightNumericValue !== null) {
        return leftNumericValue - rightNumericValue;
    }
    if (leftValue === EMPTY_VALUE && rightValue !== EMPTY_VALUE) {
        return 1;
    }
    if (rightValue === EMPTY_VALUE && leftValue !== EMPTY_VALUE) {
        return -1;
    }
    return leftValue.localeCompare(rightValue, undefined, {
        numeric: true,
        sensitivity: 'base',
    });
};
/**
 * Build normalized rows for the AI Hub list view table.
 */
exports.buildListViewRows = function (useCases) {
    return useCases.map(function (useCase) {
        var prioritisationScore = prioritisation_1.getAveragePrioritisationScore(useCase.fields.Prioritisation);
        return {
            id: useCase.id,
            name: normalizeValue(useCase.title),
            prioritisation: prioritisationScore !== null
                ? prioritisation_1.formatPrioritisationScore(prioritisationScore)
                : EMPTY_VALUE,
            complexity: getFieldValue(useCase, ['Complexity']),
            priority: getFieldValue(useCase, ['Priority']),
            owner: getFieldValue(useCase, ['Owner']),
            ownerEmail: getFieldValue(useCase, [
                'OwnerEmailAddress',
                'ownerEmailAddress',
                'OwnerEmail',
                'ownerEmail',
                'UserMail',
                'userMail',
            ]),
            stage: getFieldValue(useCase, ['Stage', 'stage']),
            sourceSystem: getSourceSystemValue(useCase),
            clientGroup: getFieldValue(useCase, ['ClientGroup']),
            businessFunction: getFieldValue(useCase, ['Area']),
            useCase: useCase,
        };
    });
};
/**
 * Resolves the badge tone class for short categorical list-view fields.
 */
exports.getListCellBadgeTone = function (columnKey, value) {
    if (!value || value === EMPTY_VALUE) {
        return null;
    }
    if (columnKey === 'complexity') {
        return getComplexityBadgeTone(value);
    }
    if (columnKey === 'prioritisation') {
        return getPrioritisationBadgeTone(value);
    }
    if (columnKey === 'priority') {
        return getPriorityBadgeTone(value);
    }
    if (columnKey === 'stage') {
        return getStageBadgeTone(value);
    }
    if (columnKey === 'clientGroup') {
        return getClientGroupBadgeTone(value);
    }
    if (columnKey === 'businessFunction') {
        return getBusinessFunctionBadgeTone(value);
    }
    if (columnKey === 'sourceSystem') {
        return 'system-neutral';
    }
    return null;
};
/**
 * Build sorted dropdown values for a single filter key.
 */
exports.getListFilterValues = function (rows, filterKey) {
    var uniqueValues = rows.reduce(function (result, row) {
        var value = row[filterKey];
        if (value && value !== EMPTY_VALUE) {
            result[value] = true;
        }
        return result;
    }, {});
    return Object.keys(uniqueValues).sort(function (leftValue, rightValue) {
        return leftValue.localeCompare(rightValue, undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    });
};
var matchesListFilters = function (row, filters) {
    return ((!filters.stage || row.stage === filters.stage) &&
        (!filters.sourceSystem || row.sourceSystem === filters.sourceSystem) &&
        (!filters.owner || row.owner === filters.owner));
};
var matchesListSearch = function (row, searchText) {
    var searchTerms = getSearchTerms(searchText);
    if (searchTerms.length === 0) {
        return true;
    }
    return searchTerms.every(function (searchTerm) {
        return SEARCHABLE_ROW_KEYS.some(function (key) {
            return ("" + row[key]).toLowerCase().indexOf(searchTerm) >= 0;
        });
    });
};
/**
 * Counts rows that would remain visible after applying one candidate filter value.
 */
exports.getListFilterResultCount = function (rows, filters, filterKey, filterValue, searchText) {
    var _a;
    var nextFilters = __assign(__assign({}, filters), (_a = {}, _a[filterKey] = filterValue, _a));
    return rows.filter(function (row) {
        return matchesListFilters(row, nextFilters) && matchesListSearch(row, searchText);
    }).length;
};
exports.getListFilterResultCounts = function (rows, filters, filterKey, searchText) {
    var _a;
    // Apply all filters except the one we are aggregating, plus the search.
    var baseFilters = __assign(__assign({}, filters), (_a = {}, _a[filterKey] = '', _a));
    var counts = { all: 0, byValue: {} };
    for (var _i = 0, rows_1 = rows; _i < rows_1.length; _i++) {
        var row = rows_1[_i];
        if (!matchesListFilters(row, baseFilters) || !matchesListSearch(row, searchText)) {
            continue;
        }
        counts.all += 1;
        var value = row[filterKey];
        counts.byValue[value] = (counts.byValue[value] || 0) + 1;
    }
    return counts;
};
/**
 * Apply filters and sorting to list view rows.
 */
exports.filterAndSortListRows = function (rows, filters, searchText, sortKey, sortDirection) {
    var filteredRows = rows.filter(function (row) {
        return matchesListFilters(row, filters) && matchesListSearch(row, searchText);
    });
    if (!sortKey) {
        return filteredRows;
    }
    return __spreadArrays(filteredRows).sort(function (leftRow, rightRow) {
        var comparison = compareRowValues(leftRow, rightRow, sortKey);
        return sortDirection === 'asc' ? comparison : comparison * -1;
    });
};
